import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Brain, 
  FileText, 
  MessageCircle, 
  CheckCircle, 
  ArrowRight,
  Star,
  Users,
  Zap,
  Shield
} from 'lucide-react';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
`;

const HeroSection = styled.section`
  padding: 4rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 4rem;
`;

const PrimaryButton = styled(Link)`
  background: white;
  color: #6366f1;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2rem;
  border: 2px solid white;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover {
    background: white;
    color: #6366f1;
    transform: translateY(-3px);
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 1rem;
  background: white;
  position: relative;
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #f3f4f6;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: 4rem 1rem;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: white;
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  text-align: center;
`;

const StatItem = styled.div`
  padding: 1rem;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #6366f1;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #d1d5db;
`;

const PricingSection = styled.section`
  padding: 6rem 1rem;
  background: #f9fafb;
`;

const PricingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const PricingCard = styled(motion.div)`
  background: white;
  padding: 3rem 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  margin: 0 auto;
  position: relative;
  border: 2px solid ${props => props.featured ? '#6366f1' : '#e5e7eb'};
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const PricingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const PricingPrice = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #6366f1;
  margin-bottom: 0.5rem;
`;

const PricingPeriod = styled.div`
  color: #6b7280;
  margin-bottom: 2rem;
`;

const PricingFeatures = styled.ul`
  list-style: none;
  text-align: left;
  margin-bottom: 2rem;
`;

const PricingFeature = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #6b7280;
`;

const Home = () => {
  return (
    <HomeContainer>
      <HeroSection>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle>
          CV’niz başvurularda
            <br />
            <span style={{ color: '#fbbf24' }}>Eleniyor</span> Olabilir
          </HeroTitle>
          <HeroSubtitle>
          Birçok CV, işe alım sistemleri tarafından daha insan görmeden eleniyor.
          CV’nizin bu riskleri taşıyıp taşımadığını hemen kontrol edin.
          </HeroSubtitle>
        </motion.div>
        
        <CTAButtons
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <PrimaryButton to="/cv-analyzer">
            <Brain size={20} />
            CV’mi Hemen Analiz Et
            <ArrowRight size={20} />
          </PrimaryButton>
          
        </CTAButtons>
      </HeroSection>

      <FeaturesSection>
        <FeaturesContainer>
          <SectionTitle>Neden İşHazır?</SectionTitle>
          <SectionSubtitle>
            AI teknolojisi ile kariyerinizde fark yaratacak özellikler
          </SectionSubtitle>
          
          <FeaturesGrid>
            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FeatureIcon>
                <FileText size={32} />
              </FeatureIcon>
              <FeatureTitle>Akıllı CV Analizi</FeatureTitle>
              <FeatureDescription>
                AI, CV'nizi Türk iş piyasasına uygun şekilde analiz eder, 
                düzenler ve profesyonelleştirir.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <FeatureIcon>
                <MessageCircle size={32} />
              </FeatureIcon>
              <FeatureTitle>Mülakat Simülasyonu</FeatureTitle>
              <FeatureDescription>
                Pozisyonunuza özel sorularla mülakat pratiği yapın ve 
                gerçek mülakata hazırlanın.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FeatureIcon>
                <Zap size={32} />
              </FeatureIcon>
              <FeatureTitle>Hızlı ve Etkili</FeatureTitle>
              <FeatureDescription>
                Dakikalar içinde profesyonel CV'nizi hazırlayın ve 
                mülakat becerilerinizi geliştirin.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </FeaturesContainer>
      </FeaturesSection>

      <StatsSection>
        <StatsContainer>
          <StatItem>
            <StatNumber>10K+</StatNumber>
            <StatLabel>Mutlu Kullanıcı</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>95%</StatNumber>
            <StatLabel>Başarı Oranı</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>AI Desteği</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>5★</StatNumber>
            <StatLabel>Kullanıcı Puanı</StatLabel>
          </StatItem>
        </StatsContainer>
      </StatsSection>

      
    </HomeContainer>
  );
};

export default Home;
